// apps/b2b/src/lib/auth.tsx
'use client';
import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { api } from './api';
import { setToken, getToken, setRefreshToken, getRefreshToken, refreshAccessToken, clearAuth } from '@tb/api-client/client';

type User = { id: string; email: string; firstName?: string; lastName?: string; displayName?: string; role?: string };
type AuthCtx = {
 user: User | null;
 loading: boolean;
 login: (email: string, password: string) => Promise<void>;
 logout: () => void;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
 const [user, setUser] = useState<User | null>(null);
 const [loading, setLoading] = useState(true);

 const doInit = useCallback(async () => {
 const t = getToken();
 if (!t) { setLoading(false); return; }
 setToken(t);
 try {
 // Try refresh first (access token may be expired)
 const rt = getRefreshToken();
 if (rt) {
 await refreshAccessToken();
 }
 const r: any = await api.me();
 setUser(r?.data?.user ?? null);
 } catch {
 // Network error — DON'T clear auth, keep stale token for retry
 // Only clear auth if we explicitly know the refresh failed
 } finally {
 setLoading(false);
 }
 }, []);

 useEffect(() => {
 doInit();

 // Proactive token refresh every 10 minutes (access TTL = 15m)
 // Keeps session alive while tab is open
 const refreshInterval = setInterval(async () => {
 const t = getToken();
 const rt = getRefreshToken();
 if (!t || !rt) return;
 try {
 await refreshAccessToken();
 } catch {
 // silent — next API call will trigger reactive refresh
 }
 }, 10 * 60 * 1000);

 // Re-validate session when tab regains focus
 const onFocus = () => {
 const t = getToken();
 const rt = getRefreshToken();
 if (t && rt) {
 refreshAccessToken().then(() => api.me().then((r: any) => setUser(r?.data?.user ?? null)).catch(() => {}));
 }
 };
 window.addEventListener('focus', onFocus);

 return () => {
 clearInterval(refreshInterval);
 window.removeEventListener('focus', onFocus);
 };
 }, [doInit]);

 const login = async (email: string, password: string) => {
 const r: any = await api.login(email, password);
 const { accessToken, refreshToken, user: u } = r?.data ?? {};
 if (accessToken) {
 setToken(accessToken);
 if (refreshToken) setRefreshToken(refreshToken);
 }
 setUser(u);
 };

 const logout = () => {
 api.logout().catch(() => {});
 clearAuth();
 setUser(null);
 };

 return <Ctx.Provider value={{ user, loading, login, logout }}>{children}</Ctx.Provider>;
}

export function useAuth() {
 const c = useContext(Ctx);
 if (!c) throw new Error('useAuth outside AuthProvider');
 return c;
}
